import { Fragment, useEffect, useRef, useState } from "react";
import "./App.css";
import PageLoader from "./components/Loader/PageLoader";
import AllRoutes from "./config/routes";
import { ThemeProvider } from "./context/ThemeContext";
import useHandleLogout from "./hooks/useHandleLogout";
import SessionExpiredModal from "./components/Modal/SessionExpiredModal";
import {
  endSession,
  hydrateFromStorage,
  persistTokenPair,
} from "./lib/auth/refreshManager";
import { exchangeCode, fetchMe } from "./lib/auth/session.api";
import {
  usePushNotificationMutation,
  useUserProfileMutation,
} from "./lib/endpoints/auth/auth.api";
import { addUserProfile, selectUserState } from "./lib/features/auth/authSlice";
import { store, useAppDispatch, useTypedSelector } from "./lib/store/store";
import CalendlyScheduleModal from "./components/Modal/CalendlySchedule";
import { generateToken, messaging } from "./lib/utils/firebase";
import { onMessage } from "firebase/messaging";
import GoogleAnalytics from "./components/GoogleAnalytics";
import HotJar from "./components/Hotjar";
import { hotjar } from "react-hotjar";
import { GOOGLE_ANALYTICS, HOTJAR_ID, TOKEN } from "./utils/constants";
import ReactGA from "react-ga4";

import CompleteProfileUpdate from "./components/Modal/CompleteProfileUpdate";
import { isMidOrAbove } from "./lib/levels";
import { GoogleMapsProvider } from "./lib/contexts/google-map-context";
import { ManageAccountProvider } from "./context/ManageAccountContext";
import { AppToastContainer } from "./components/Alert";

/* The storage key now lives in utils/constants so the auth modules can
   import it without pulling in the whole App tree. Re-exported for any
   caller still importing it from here. */
export { TOKEN } from "./utils/constants";

function App() {
  const userState = useTypedSelector(selectUserState);
  const dispatch = useAppDispatch();
  const { getProfile } = useUserProfileMutation();
  const { subscribe } = usePushNotificationMutation();
  const { handleLogout } = useHandleLogout();
  const [modalInterview, setModalInterview] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  const handlePushNotificationToken = async () => {
    const token = await generateToken();

    if (!token) return;

    const formData = new FormData();
    formData.append("push_token", token);

    try {
      !isSubscribed &&
        subscribe(formData, {
          onSuccess: (res) => {
            setIsSubscribed(true);
            // console.log('response ', res)
          },
          onError: (error: any) => {
            console.log("error ", error);
          },
        });
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (process.env.NODE_ENV === "developement") return;
    hotjar.initialize({ id: HOTJAR_ID, sv: 6 });
    ReactGA.initialize(GOOGLE_ANALYTICS);
    ReactGA.send({ hitType: "pageview", page: window?.location?.pathname });
  }, []);

  useEffect(() => {
    if (isMounted && !!userState.accessToken) {
      handlePushNotificationToken();
      onMessage(messaging, (payload) => {
        new Notification(payload.notification?.title ?? "", {
          icon: payload.notification?.icon ?? "./logo.png",
          body: payload.notification?.body ?? "",
        });
      });

      if (
        !!userState?.user?.cv_public_id &&
        !!userState?.user?.photo_public_id &&
        !!userState?.user?.userLocation &&
        !userState.user?.userCbtSoftSkill?.is_passed &&
        isMidOrAbove(userState.user?.userCbtLevel)
      ) {
        setModalInterview(true);
      }
    } else {
      setIsMounted(true);
    }
  }, [isMounted, userState]);

  /* Session bootstrap. Four ways in, checked in this order:
       1. ?code=   one-time handoff from welcome -> /auth/exchange (new)
       2. stored refresh token                   -> /auth/me       (new)
       3. ?token= or stored legacy JWT           -> getUserProfile (legacy)
       4. nothing                                -> off to welcome
     Both credential params are read and stripped from the URL before any
     branch runs, whichever path ends up used: a live credential in the
     address bar ends up in browser history and in anything that records
     page URLs (Hotjar included). replaceState rewrites the current history
     entry, so there is no back-button copy either. Other params are
     preserved. */
  /* React 18 StrictMode runs this effect twice in development (mount,
     simulated unmount, mount again). The first run strips ?code= from the
     URL and starts the exchange; a second run would then find no code and
     no stored session yet, and fall through to logout, bouncing a freshly
     logged-in user back to welcome. Production builds run it once. */
  const bootstrapped = useRef(false);
  useEffect(() => {
    if (bootstrapped.current) return;
    bootstrapped.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get("code")?.trim() ?? "";
    const legacyToken = params.get("token")?.trim() ?? "";

    if (params.has("code") || params.has("token")) {
      params.delete("code");
      params.delete("token");
      const query = params.toString();
      window.history.replaceState(
        {},
        document.title,
        window.location.pathname +
          (query ? `?${query}` : "") +
          window.location.hash
      );
    }

    try {
      if (code) {
        exchangeCode(code)
          .then(({ accessToken, refreshToken, expiresIn, user }) => {
            persistTokenPair({ accessToken, refreshToken, expiresIn });
            dispatch(addUserProfile({ accessToken, refreshToken, user }));
          })
          .catch((error: any) => {
            /* CODE_USED / CODE_EXPIRED / CODE_INVALID are all terminal: the
               code is single-use and lives 60s, so there is nothing to retry.
               A network failure leaves the loader; the user can reload. */
            if (error?.response?.status === 401) endSession("unknown");
          });
        return;
      }

      if (hydrateFromStorage()) {
        if (userState.accessToken && userState.user) return;

        fetchMe()
          .then((user) => {
            const { accessToken, refreshToken } = store.getState().auth;
            dispatch(addUserProfile({ accessToken, refreshToken, user }));
          })
          .catch(() => {
            /* A 401 here means the interceptor already tried to refresh and
               failed, and endSession has raised the modal. Anything else is
               a network or server fault, and the session is still valid. */
          });
        return;
      }

      if (legacyToken) {
        const formData = new FormData();
        const token = legacyToken;
        formData.append("userToken", token);

        getProfile(formData, {
          onSuccess: (res) => {
            const userInfo = {
              accessToken: res.accessToken,
              user: {
                ...res.user.data[0],
                extraDetail: res.user.data[1],
              },
            };
            dispatch(addUserProfile(userInfo));
          },
          /* A handoff token the API rejects is dead on arrival -- there is no
             refresh token at this point, so the only way forward is a fresh
             login. Swallowing this used to strand the user on the loader
             below, forever. */
          onError: (error: any) => {
            if (error?.response?.status === 401) endSession("unknown");
          },
        });
        localStorage.setItem(TOKEN, token ?? "");
      } else if (localStorage.getItem(TOKEN)) {
        if (userState.accessToken) return;

        const formData = new FormData();
        formData.append("userToken", localStorage.getItem(TOKEN) ?? "");
        getProfile(formData, {
          onSuccess: (res) => {
            const userInfo = {
              accessToken: res.accessToken,
              user: {
                ...res.user.data[0],
                extraDetail: res.user.data[1],
              },
            };
            dispatch(addUserProfile(userInfo));
          },
          /* A stored legacy JWT (no refresh token alongside it) that the API
             now rejects. Nothing to refresh with; raise the modal instead of
             the endless loader. Sessions started via ?code= never come
             through here -- they hydrate from the stored pair above. */
          onError: (error: any) => {
            if (error?.response?.status === 401) endSession("unknown");
          },
        });
      } else {
        handleLogout();
      }
    } catch (error) {
      console.log(error);
    }
  }, []);

  /* The modal has to render alongside the loader, not inside the tree below
     it: an expired session never satisfies this guard, so anything rendered
     only past it could never be seen. */
  if (userState.accessToken === null || userState?.user?.role !== "developer")
    return (
      <Fragment>
        <SessionExpiredModal />
        {/* Once the session has ended there is nothing left to wait for, and
            the spinner sitting over the modal made it read as busy rather
            than as something to act on. */}
        {userState.sessionExpiredReason ? null : <PageLoader />}
      </Fragment>
    );

  return (
    <>
      <GoogleAnalytics />
      <HotJar />
      <AppToastContainer />

      <GoogleMapsProvider apiKey={process.env.REACT_APP_GOOGLE_MAP_API ?? ""}>
        <ThemeProvider>
          <ManageAccountProvider>
            <div className="relative">
              <SessionExpiredModal />
              <AllRoutes />

              {/* Decides for itself whether to show, from the API's
                  incomplete-profile tasks, and steps aside on /profile. */}
              <CompleteProfileUpdate />


            {/* Schedule-interview blocker disabled. Not hidden via CSS on purpose:
                ModalContainer always mounts a full-viewport fixed backdrop at z-30,
                so hiding it visually would leave an invisible click-blocking layer.
                Restore by uncommenting this block. */}
            {/* {!userState.user?.userCbtSoftSkill?.is_passed && modalInterview && (
              userState.user?.userCbtLevel?.level_name === "senior" ||
              userState.user?.userCbtLevel?.level_name === "mid"
            ) && (
              <CalendlyScheduleModal
                close={() => setModalInterview(false)}
                open
              />
            )} */}
            </div>
          </ManageAccountProvider>
        </ThemeProvider>
      </GoogleMapsProvider>
    </>
  );
}

export default App;
